import type { ComponentType, ForwardedRef, ReactElement, ReactNode } from "react";
import { forwardRef } from "react";

import type {
    IDropDownItem,
    IDropdownChild } from "@similarweb/ui-components";
import {
    Dropdown,
    DropdownButton,
    CountryDropdownItem,
} from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import type { ICountryObject } from "app/services/CountryService";

import { StyledBaseDropdownButtonWrap, StyledDropdownContainer } from "../../common-styles";

import { StyledMultiSelectCountryDropdownItem } from "./styles";

type CountriesDropdownProps = {
    multi?: boolean;
    width?: number;
    appendTo?: string;
    buttonText?: string;
    virtualize?: boolean;
    placeholder: string;
    isOpenedByDefault?: boolean;
    itemWrapper?: ComponentType<any>;
    items: readonly IDropDownItem[];
    dropdownPopupHeight?: number;
    dropdownPopupPlacement?: string;
    selectedItems: readonly IDropDownItem["id"][];
    disabled?: boolean;
    headerComponent?: ReactNode;
    onToggle?(isOpened: boolean, isOutsideClick: boolean): void;
    onSelect(country: ICountryObject | null): void;
    renderButton?(): ReactNode;
    renderDDItem?(item: IDropDownItem, index?: number): ReactNode;
};

type IDropdownChildWithChildren = IDropdownChild & {
    children?: Array<{
        props?: {
            name?: string;
        };
    }>;
};

/**
 * @deprecated Legacy eager-rendering country dropdown (renders its whole option list up front). For
 * filter-panel country selects use `CountrySelectCombobox` from
 * `common-components/dropdown/CountrySelectCombobox`, which mounts options lazily. Still used by a few
 * non-filter surfaces (export modal, AI agent knowledge base) that depend on `headerComponent`.
 */
function CountriesDropdown<Ref>(props: CountriesDropdownProps, ref: ForwardedRef<Ref>) {
    const {
        items,
        width,
        selectedItems,
        buttonText,
        placeholder,
        onToggle,
        onSelect,
        renderButton,
        renderDDItem,
        appendTo,
        multi = false,
        dropdownPopupHeight,
        dropdownPopupPlacement,
        isOpenedByDefault = false,
        virtualize,
        itemWrapper,
        disabled = false,
        headerComponent = null,
    } = props;
    const selectedIds = selectedItems.reduce<Record<number, boolean>>((map, id) => {
        map[id as number] = true;
        return map;
    }, {});
    const DropdownItemComponent = multi ? StyledMultiSelectCountryDropdownItem : CountryDropdownItem;

    const handleSearch = (term: string, item: ReactElement) => {
        return (
            (item.props as IDropDownItem).text?.toLowerCase().includes(term.toLowerCase()) ||
            (item?.props as IDropdownChildWithChildren)?.children?.[0]?.props?.name
                ?.toLowerCase()
                .includes(term.toLowerCase()) ||
            // @ts-expect-error error TS2339: Property 'text' does not exist on type '{ name?: string; }'.
            (item?.props as IDropdownChildWithChildren)?.children?.[0]?.props?.text
                ?.toLowerCase()
                .includes(term.toLowerCase())
        );
    };

    const renderDDButton = () => {
        if (typeof renderButton === "function") {
            return renderButton();
        }

        const buttonTextTranslationKey = `si.components.countries_dd.button.text${multi ? "" : ".single"}`;
        const displayedButtonText =
            typeof buttonText === "undefined" ? translate(buttonTextTranslationKey) : buttonText;

        return (
            <StyledBaseDropdownButtonWrap key="countries-dropdown-button">
                <DropdownButton>{displayedButtonText}</DropdownButton>
            </StyledBaseDropdownButtonWrap>
        );
    };

    function renderItem(item: IDropDownItem, index?: number) {
        if (typeof renderDDItem === "function") {
            return renderDDItem(item, index);
        }

        return (
            <DropdownItemComponent
                key={item.id}
                {...item}
                wrapper={itemWrapper}
            />
        );
    }

    return (
        <StyledDropdownContainer>
            <Dropdown
                // @ts-expect-error Wrong type
                ref={ref}
                hasSearch
                width={width}
                onClick={onSelect}
                onToggle={onToggle}
                appendTo={appendTo}
                itemsComponent={DropdownItemComponent}
                closeOnItemClick={!multi}
                selectedIds={selectedIds}
                searchFilter={handleSearch}
                itemWrapper={itemWrapper}
                virtualize={virtualize ?? items.length > 10}
                defaultOpen={isOpenedByDefault}
                searchPlaceHolder={placeholder}
                dropdownPopupHeight={dropdownPopupHeight}
                dropdownPopupPlacement={dropdownPopupPlacement}
                disabled={disabled}
                headerComponent={headerComponent}
            >
                {[renderDDButton(), ...items.map(renderItem)]}
            </Dropdown>
        </StyledDropdownContainer>
    );
}

export default forwardRef(CountriesDropdown);
